import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  name?: string;
  /** Show gradient AI look. */
  ai?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Show editor-style outline ring on hover. */
  editable?: boolean;
}

const sizeStyles = {
  sm: 'w-7 h-7 text-xs',
  md: 'w-9 h-9 text-sm',
  lg: 'w-12 h-12 text-base',
} as const;

/**
 * User / AI avatar. Initials shown; gradient variant for AI assistant.
 */
export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ name = '?', ai = false, size = 'md', editable, className, ...rest }, ref) => {
    const initials = name
      .split(/\s+/)
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-shrink-0 items-center justify-center rounded-full font-semibold',
          ai
            ? 'bg-gradient-to-br from-brand-300 to-brand-500 font-serif text-white'
            : 'bg-brand-100 text-brand-700',
          sizeStyles[size],
          editable && 'cursor-pointer ring-0 transition-shadow hover:ring-2 hover:ring-brand-400',
          className
        )}
        aria-label={name}
        {...rest}
      >
        {initials}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';
